import React, { useCallback, useEffect, useState } from "react";
import { HardDrive, RefreshCw, Loader2 } from "lucide-react";
import { storageApi } from "../lib/api";
import { formatBytesTR, formatPercentTR, formatDateTimeTR } from "../lib/storageFormat";

/** Ayarlar → Depolama. Yalnızca ölçüm gösterir; hiçbir dosyaya dokunmaz. */
export const StorageUsageCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(false);
    try {
      setData(await storageApi.usage(refresh));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const quota = data?.quota_bytes || 1;
  const activePct = data ? Math.min(100, (data.active_bytes * 100) / quota) : 0;
  const orphanPct = data ? Math.min(100 - activePct, (data.orphan_bytes * 100) / quota) : 0;

  return (
    <div className="rounded-lg border border-white/10 bg-sertex-surface/60 p-4 space-y-3" data-testid="storage-usage-card">
      <div className="flex items-center gap-2">
        <HardDrive className="h-4 w-4 text-sertex-cyan" />
        <span className="text-sertex-text font-semibold">Depolama</span>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={loading}
          data-testid="storage-usage-refresh"
          className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-white/10 text-sertex-textMuted hover:text-sertex-cyan hover:border-sertex-cyan/40 text-[11px] transition-colors disabled:opacity-40"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Yenile
        </button>
      </div>

      {error ? (
        <div className="text-[12px] text-amber-300" data-testid="storage-usage-error">
          Şu an hesaplanamadı. Biraz sonra "Yenile"yi deneyin.
        </div>
      ) : !data ? (
        <div className="hud-text text-sertex-textMuted normal-case" data-testid="storage-usage-loading">
          HESAPLANIYOR...
        </div>
      ) : (
        <>
          <div className="text-sertex-text text-sm tabular-nums" data-testid="storage-usage-total">
            {formatBytesTR(data.total_bytes)} / {formatBytesTR(data.quota_bytes)} ({formatPercentTR(data.used_percent)})
          </div>

          <div className="h-3 w-full rounded-full bg-white/10 overflow-hidden flex" data-testid="storage-usage-bar">
            <div className="h-full bg-sertex-cyan" style={{ width: `${activePct}%` }} data-testid="storage-usage-bar-active" />
            <div className="h-full bg-amber-400" style={{ width: `${orphanPct}%` }} data-testid="storage-usage-bar-orphan" />
          </div>

          <div className="flex items-center gap-4 text-[11px] text-sertex-textSecondary">
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-sm bg-sertex-cyan" /> Aktif dosyalar
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-sm bg-amber-400" /> Silinen görevlerden kalanlar
            </span>
          </div>

          <div className="text-[12px] text-sertex-textSecondary tabular-nums" data-testid="storage-usage-counts">
            Aktif: {data.active_files} dosya ({formatBytesTR(data.active_bytes)}) · Çöp: {data.orphan_files} dosya ({formatBytesTR(data.orphan_bytes)})
          </div>

          <p className="text-[11px] text-sertex-textMuted leading-relaxed">
            Bir görevi kalıcı olarak sildiğinizde, o göreve eklediğiniz dosyalar dosya
            deposundan kaldırılamıyor: kullandığımız depolama hizmeti dosya silmeye izin
            vermiyor. Bu dosyalar artık hiçbir görevde görünmez ama yer kaplamaya devam
            eder; yukarıdaki sarı bölüm bu birikmiş dosyaları gösterir.
          </p>

          <div className="text-[10px] text-sertex-textMuted" data-testid="storage-usage-meta">
            Son hesaplama: {formatDateTimeTR(data.computed_at)} · Yöntem:{" "}
            {data.method === "storage_list" ? "dosya deposu sayımı" : "veritabanı kayıtları"}
          </div>
        </>
      )}
    </div>
  );
};

export default StorageUsageCard;
